import type { ForqSupabaseClient } from './client';
import type {
  AccessRequestStatus,
  AdminAccessRequestRecord,
  AdminAnnouncementRecord,
  AdminAuditRecord,
  AdminGateway,
  AdminInvitationOutcome,
  AdminInvitationRecord,
  AdminInvitationStatus,
  AdminNumberSequenceRecord,
  AdminPermissionGroup,
  AdminPermissionRecord,
  AdminRoleRecord,
  AdminSnapshot,
  AdminUserRecord,
  DecideAccessRequestInput,
  InviteAdminUserInput,
  SaveAnnouncementInput,
  UnassignedUserRecord,
} from '@forq/admin-data';
import { SupabaseOperationError } from './errors';
import { mapAnnouncement } from './platform';
import type { AnnouncementRow } from './platform';

interface RpcUser {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly roleIds: readonly string[];
  readonly active: boolean;
  readonly isCurrentUser: boolean;
  readonly lastLoginAt: string | null;
}

interface RpcRole {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly isSystem: boolean;
  readonly userCount: number;
  readonly permissionKeys: readonly string[];
}

interface RpcPermission {
  readonly key: string;
  readonly moduleId: string;
  readonly label: string;
}

interface RpcNumberSequence {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly year: number | null;
  readonly prefix: string;
  readonly sequenceLength: number;
  readonly nextValue: number | string;
  readonly requiredPermission: string;
  readonly active: boolean;
}

interface RpcAudit {
  readonly id: string;
  readonly actor: string;
  readonly action: string;
  readonly entity: string;
  readonly detail: string;
  readonly occurredAt: string;
}

interface RpcInvitation {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly roleIds: readonly string[];
  readonly status: string;
  readonly errorMessage: string | null;
  readonly createdAt: string;
}

interface RpcSnapshot {
  readonly tenantId: string;
  readonly users: readonly RpcUser[];
  readonly roles: readonly RpcRole[];
  readonly permissions: readonly RpcPermission[];
  readonly numberSequences: readonly RpcNumberSequence[];
  readonly audit: readonly RpcAudit[];
  readonly generatedAt: string;
}

const moduleLabels: Readonly<Record<string, string>> = {
  admin: 'Administration',
  customers: 'Kunden',
  dashboard: 'Dashboard',
  machines: 'Maschinen',
  masterdata: 'Stammdaten',
  notifications: 'Benachrichtigungen',
  orders: 'Orders',
  platform: 'Plattform',
  studio: 'FORQ Studio',
  tasks: 'Aufgaben',
};

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function groupPermissions(records: readonly RpcPermission[]): readonly AdminPermissionGroup[] {
  const grouped = new Map<string, AdminPermissionRecord[]>();
  for (const record of records) {
    const permissions = grouped.get(record.moduleId) ?? [];
    permissions.push({ key: record.key, moduleId: record.moduleId, label: record.label });
    grouped.set(record.moduleId, permissions);
  }

  return [...grouped.entries()]
    .sort(([left], [right]) => left.localeCompare(right, 'de'))
    .map(([moduleId, permissions]) => ({
      moduleId,
      label: moduleLabels[moduleId] ?? moduleId,
      permissions: permissions.sort((left, right) => left.label.localeCompare(right.label, 'de')),
    }));
}

function invitationStatus(value: string): AdminInvitationStatus {
  if (value === 'pending' || value === 'sent' || value === 'assigned' || value === 'failed') return value;
  throw new SupabaseOperationError('admin.invitations', `Unsupported invitation status "${value}".`);
}

function mapInvitations(value: unknown): readonly AdminInvitationRecord[] {
  if (!Array.isArray(value)) {
    throw new SupabaseOperationError('admin.invitations', 'The invitation response has an invalid shape.');
  }

  return (value as RpcInvitation[]).map((invitation) => ({
    id: invitation.id,
    email: invitation.email,
    displayName: invitation.displayName,
    roleIds: [...(invitation.roleIds ?? [])],
    status: invitationStatus(invitation.status),
    errorMessage: invitation.errorMessage ?? null,
    createdAt: invitation.createdAt,
  }));
}

interface RpcAccessRequest {
  readonly id: string;
  readonly email: string;
  readonly display_name: string;
  readonly note: string | null;
  readonly status: string;
  readonly created_at: string;
  readonly decided_at: string | null;
  readonly decided_by_name: string | null;
  readonly decision_note: string | null;
}

function accessRequestStatus(value: string): AccessRequestStatus {
  return value === 'approved' || value === 'rejected' ? value : 'pending';
}

function mapAccessRequests(value: unknown): readonly AdminAccessRequestRecord[] {
  if (!Array.isArray(value)) {
    throw new SupabaseOperationError('admin.accessRequests', 'The access request response has an invalid shape.');
  }

  // Diese RPC liefert snake_case (row_to_json auf der Tabelle), anders als admin_get_snapshot.
  return (value as RpcAccessRequest[]).map((request) => ({
    id: request.id,
    email: request.email,
    displayName: request.display_name,
    note: request.note ?? '',
    status: accessRequestStatus(request.status),
    createdAt: request.created_at,
    decidedAt: request.decided_at ?? null,
    decidedByName: request.decided_by_name ?? null,
    decisionNote: request.decision_note ?? '',
  }));
}

function mapAnnouncements(value: unknown): readonly AdminAnnouncementRecord[] {
  if (!Array.isArray(value)) {
    throw new SupabaseOperationError('admin.announcements', 'The announcement response has an invalid shape.');
  }

  // Ebenfalls snake_case (row_to_json). mapAnnouncement ist dieselbe Uebersetzung, die der Start
  // benutzt -- bewusst geteilt, damit Adminvorschau und echtes Startfenster nicht auseinanderlaufen.
  return (value as (AnnouncementRow & { active: boolean; updated_by_name: string | null })[])
    .map((row) => ({
      ...mapAnnouncement(row),
      active: Boolean(row.active),
      updatedByName: row.updated_by_name ?? null,
    }));
}

function mapUnassignedUsers(value: unknown): readonly UnassignedUserRecord[] {
  if (!Array.isArray(value)) {
    throw new SupabaseOperationError('admin.unassignedUsers', 'The unassigned users response has an invalid shape.');
  }
  return (value as { readonly id: string; readonly displayName: string; readonly email: string | null; readonly createdAt: string; readonly lastLoginAt: string | null }[]).map((user) => ({
    id: user.id,
    displayName: user.displayName,
    email: user.email ?? '',
    createdAt: user.createdAt,
    lastLoginAt: user.lastLoginAt ?? null,
  }));
}

function mapSnapshot(
  value: unknown,
  invitations: readonly AdminInvitationRecord[],
  accessRequests: readonly AdminAccessRequestRecord[],
  announcements: readonly AdminAnnouncementRecord[],
  unassignedUsers: readonly UnassignedUserRecord[],
): AdminSnapshot {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError('admin.snapshot', 'The admin snapshot has an invalid shape.');
  }

  const row = value as RpcSnapshot;
  const users: readonly AdminUserRecord[] = (row.users ?? []).map((user) => ({
    id: user.id,
    displayName: user.displayName,
    email: user.email ?? '',
    roleIds: [...(user.roleIds ?? [])],
    active: Boolean(user.active),
    isCurrentUser: Boolean(user.isCurrentUser),
    lastLoginAt: user.lastLoginAt ?? null,
  }));
  const roles: readonly AdminRoleRecord[] = (row.roles ?? []).map((role) => ({
    id: role.id,
    name: role.name,
    description: role.description ?? '',
    isSystem: Boolean(role.isSystem),
    userCount: Number(role.userCount ?? 0),
    permissionKeys: [...(role.permissionKeys ?? [])],
  }));
  const numberSequences: readonly AdminNumberSequenceRecord[] = (row.numberSequences ?? []).map((sequence) => ({
    id: sequence.id,
    key: sequence.key,
    label: sequence.label,
    year: sequence.year ?? null,
    prefix: sequence.prefix,
    sequenceLength: Number(sequence.sequenceLength),
    nextValue: Number(sequence.nextValue),
    requiredPermission: sequence.requiredPermission,
    active: Boolean(sequence.active),
  }));
  const audit: readonly AdminAuditRecord[] = (row.audit ?? []).map((record) => ({
    id: record.id,
    actor: record.actor,
    action: record.action,
    entity: record.entity,
    detail: record.detail,
    occurredAt: record.occurredAt,
  }));

  return {
    tenantId: row.tenantId,
    users,
    invitations,
    accessRequests,
    unassignedUsers,
    announcements,
    roles,
    permissionGroups: groupPermissions(row.permissions ?? []),
    numberSequences,
    audit,
    generatedAt: row.generatedAt,
  };
}

export class SupabaseAdminGateway implements AdminGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async rpc(functionName: string, args: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await this.client.rpc(functionName, args);
    if (error) throw new SupabaseOperationError(`admin.${functionName}`, messageOf(error));
    return data;
  }

  async loadSnapshot(tenantId: string): Promise<AdminSnapshot> {
    const [snapshot, invitations, accessRequests, announcements, unassignedUsers] = await Promise.all([
      this.rpc('admin_get_snapshot', { p_tenant_id: tenantId }),
      this.rpc('admin_list_user_invitations', { p_tenant_id: tenantId }),
      this.rpc('admin_list_access_requests', { p_tenant_id: tenantId }),
      this.rpc('admin_list_announcements', { p_tenant_id: tenantId }),
      this.rpc('admin_list_unassigned_users', { p_tenant_id: tenantId }),
    ]);
    return mapSnapshot(
      snapshot,
      mapInvitations(invitations),
      mapAccessRequests(accessRequests),
      mapAnnouncements(announcements),
      mapUnassignedUsers(unassignedUsers),
    );
  }

  async saveAnnouncement(input: SaveAnnouncementInput): Promise<AdminSnapshot> {
    await this.rpc('admin_save_announcement', {
      p_tenant_id: input.tenantId,
      p_id: input.id,
      p_title: input.title,
      p_body: input.body,
      p_tone: input.tone,
      p_repeat_mode: input.repeat,
      p_active: input.active,
    });
    return this.loadSnapshot(input.tenantId);
  }

  async deleteAnnouncement(tenantId: string, announcementId: string): Promise<AdminSnapshot> {
    await this.rpc('admin_delete_announcement', {
      p_tenant_id: tenantId,
      p_id: announcementId,
    });
    return this.loadSnapshot(tenantId);
  }

  async decideAccessRequest(input: DecideAccessRequestInput): Promise<AdminSnapshot> {
    await this.rpc('admin_decide_access_request', {
      p_tenant_id: input.tenantId,
      p_request_id: input.requestId,
      p_approve: input.approve,
      p_note: input.note ?? '',
    });
    return this.loadSnapshot(input.tenantId);
  }

  async inviteUser(input: InviteAdminUserInput): Promise<AdminInvitationOutcome> {
    const { data, error } = await this.client.functions.invoke('admin-invite-user', {
      body: {
        tenantId: input.tenantId,
        email: input.email,
        displayName: input.displayName,
        roleIds: [...input.roleIds],
      },
    });
    if (error) throw new SupabaseOperationError('admin.inviteUser', messageOf(error));
    if (!data || typeof data !== 'object' || (data as { readonly ok?: boolean }).ok !== true) {
      const responseMessage = data && typeof data === 'object'
        ? (data as { readonly message?: string }).message
        : undefined;
      throw new SupabaseOperationError('admin.inviteUser', responseMessage ?? 'The invitation function returned no success response.');
    }
    // Ein Server ohne Mailversand (FORQ Server) legt das Konto selbst an und
    // gibt das Anfangspasswort zurück, statt eine Einladung zu verschicken.
    // Supabase schickt eine Mail und liefert dieses Feld nicht.
    const initialPassword = (data as { readonly initialPassword?: unknown }).initialPassword;
    return {
      snapshot: await this.loadSnapshot(input.tenantId),
      initialPassword: typeof initialPassword === 'string' && initialPassword.length > 0
        ? initialPassword
        : null,
    };
  }

  async setUserActive(tenantId: string, userId: string, active: boolean): Promise<AdminSnapshot> {
    await this.rpc('admin_set_user_active', {
      p_tenant_id: tenantId,
      p_user_id: userId,
      p_active: active,
    });
    return this.loadSnapshot(tenantId);
  }

  async setUserRoles(tenantId: string, userId: string, roleIds: readonly string[]): Promise<AdminSnapshot> {
    await this.rpc('admin_set_user_roles', {
      p_tenant_id: tenantId,
      p_user_id: userId,
      p_role_ids: [...roleIds],
    });
    return this.loadSnapshot(tenantId);
  }

  async assignUser(tenantId: string, userId: string, roleIds: readonly string[]): Promise<AdminSnapshot> {
    await this.rpc('admin_assign_user_to_tenant', {
      p_tenant_id: tenantId,
      p_user_id: userId,
      p_role_ids: [...roleIds],
    });
    return this.loadSnapshot(tenantId);
  }

  async setRolePermission(
    tenantId: string,
    roleId: string,
    permissionKey: string,
    granted: boolean,
  ): Promise<AdminSnapshot> {
    await this.rpc('admin_set_role_permission', {
      p_tenant_id: tenantId,
      p_role_id: roleId,
      p_permission_key: permissionKey,
      p_granted: granted,
    });
    return this.loadSnapshot(tenantId);
  }

  async setNumberSequenceActive(
    tenantId: string,
    sequenceId: string,
    active: boolean,
  ): Promise<AdminSnapshot> {
    await this.rpc('admin_set_number_sequence_active', {
      p_tenant_id: tenantId,
      p_sequence_id: sequenceId,
      p_active: active,
    });
    return this.loadSnapshot(tenantId);
  }
}
